import { IonContent, IonPage } from '@ionic/react';
import { useCallback } from 'react';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { SwiperSlide } from 'swiper/react';

import styles from '@components/ActivateTopicPage.module.css';
import Slides from '@components/Slides';
import { ClientTopicOfInterestEnum } from '@typing/Generated';
import { ImageName, imageUrl } from '@utils/imageUtils';
import storage from '@utils/storageUtils';

type Props = {
  topic: string;
};

const FocusArea = ({ focusArea, topic }: { focusArea: string; topic: string }) => {
  const intl = useIntl();

  const formattedFocusArea = focusArea.toLowerCase()[0].toUpperCase() + focusArea.toLowerCase().substring(1);
  return (
    <div className={styles.focusArea}>
      <div className={styles.focusAreaImageWrapper}>
        <img
          alt={intl.formatMessage({ id: 'accessibility.altTags.activateFeatureFamily' })}
          className={styles.focusAreaImage}
          src={imageUrl(`activateTopicPageFocusArea${formattedFocusArea}` as ImageName)}
        />
      </div>
      <div className={styles.focusAreaDetails}>
        <div className={styles.focusAreaName}>
          <FormattedMessage id={`pages.activate.topicPages.focusAreas.${focusArea}`} />
        </div>
        <div className={styles.bodyCopy}>
          <FormattedMessage id={`pages.activate.topicPages.${topic}.focusAreas.${focusArea}`} />
        </div>
      </div>
    </div>
  );
};

const ActivateTopicPage = ({ topic }: Props) => {
  const intl = useIntl();
  const history = useHistory();

  const formattedTopic = topic.toLowerCase()[0].toUpperCase() + topic.toLowerCase().substring(1);

  const generateForwardToSignup = useCallback(
    (topic: ClientTopicOfInterestEnum) => () => {
      if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
        ReactGA.event('activate-click', { topicOfInterest: topic });
      }
      storage.set('topicOfInterest', topic).then(() => {
        history.push('/sign-up');
      });
    },
    [history]
  );

  return (
    <IonPage>
      <Helmet>
        <title>{intl.formatMessage({ id: 'pageTitles.unauthenticated.activate.show' })}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link crossOrigin={''} rel="preconnect" href="https://fonts.gstatic.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Mulish&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <IonContent>
        <div className={styles.headerBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.grayceLogo}>
              <img alt={intl.formatMessage({ id: 'accessibility.altTags.purpleLogo' })} src={imageUrl('logoPurple')} />
            </div>
            <div className={styles.headerTopRow}>
              <div className={styles.headerFeatureImage}>
                <img
                  alt={intl.formatMessage({ id: 'accessibility.altTags.activateFeatureFamily' })}
                  src={imageUrl(`activateTopicPageTop${formattedTopic}` as ImageName)}
                />
              </div>
              <div className={styles.headerIntro}>
                <div className={styles.hugeText}>
                  <FormattedMessage id={`pages.activate.topicPages.${formattedTopic}.title`} />
                </div>
                <div className={styles.bodyCopy}>
                  <FormattedMessage id={`pages.activate.topicPages.${formattedTopic}.subtitle`} />
                </div>
                <div
                  className={styles.activateButton}
                  onClick={generateForwardToSignup(topic.toUpperCase() as ClientTopicOfInterestEnum)}
                >
                  <FormattedMessage id={`pages.activate.topicPages.activateButton`} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.careSupportBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.hugeText}>
              <FormattedMessage id={`pages.activate.topicPages.holisticCare`} />
            </div>
            <div className={styles.bodyCopy}>
              <FormattedMessage id={`pages.activate.topicPages.${formattedTopic}.holisticExplanation`} />
            </div>
          </div>
        </div>

        <div className={`${styles.focusAreasBlock} ${styles.focusAreasSlideshow}`}>
          <div className={styles.contentHolder}>
            <Slides autoplay={{ delay: 7000 }} loop slidesPerView={1}>
              {formattedTopic === 'Grieving' ? (
                <>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="wellbeing" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="financial" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="functional" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="social" topic={formattedTopic} />
                  </SwiperSlide>
                </>
              ) : (
                <>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="legal" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="medical" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="financial" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="social" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="functional" topic={formattedTopic} />
                  </SwiperSlide>
                  <SwiperSlide className={styles.slide}>
                    <FocusArea focusArea="wellbeing" topic={formattedTopic} />
                  </SwiperSlide>
                </>
              )}
            </Slides>
            <div
              className={styles.activateButton}
              onClick={generateForwardToSignup(topic.toUpperCase() as ClientTopicOfInterestEnum)}
            >
              <FormattedMessage id={`pages.activate.topicPages.activateButton`} />
            </div>
            <div className={styles.disclaimerWrapper}>
              <div className={styles.disclaimer}>
                <FormattedMessage id={`pages.activate.topicPages.${formattedTopic}.disclaimer`} />
              </div>
            </div>
          </div>
        </div>

        <div className={`${styles.focusAreasBlock} ${styles.focusAreasGrid}`}>
          <div className={styles.contentHolder}>
            {formattedTopic === 'Grieving' ? (
              <>
                <div className={styles.focusAreasRow}>
                  <div className={styles.focusAreasColumn}>
                    <FocusArea focusArea="wellbeing" topic={formattedTopic} />
                  </div>
                  <div className={styles.focusAreasColumn}>
                    <FocusArea focusArea="financial" topic={formattedTopic} />
                    <FocusArea focusArea="functional" topic={formattedTopic} />
                    <FocusArea focusArea="social" topic={formattedTopic} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={styles.focusAreasRow}>
                  <FocusArea focusArea="legal" topic={formattedTopic} />
                  <FocusArea focusArea="medical" topic={formattedTopic} />
                </div>

                <div className={styles.focusAreasRow}>
                  <FocusArea focusArea="financial" topic={formattedTopic} />
                  <FocusArea focusArea="social" topic={formattedTopic} />
                </div>

                <div className={styles.focusAreasRow}>
                  <FocusArea focusArea="functional" topic={formattedTopic} />
                  <FocusArea focusArea="wellbeing" topic={formattedTopic} />
                </div>
              </>
            )}

            <div
              className={styles.activateButton}
              onClick={generateForwardToSignup(topic.toUpperCase() as ClientTopicOfInterestEnum)}
            >
              <FormattedMessage id={`pages.activate.topicPages.activateButton`} />
            </div>
            <div className={styles.disclaimerWrapper}>
              <div className={styles.disclaimer}>
                <FormattedMessage id={`pages.activate.topicPages.${formattedTopic}.disclaimer`} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.featuresBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.hugeText}>
              <FormattedMessage id="pages.activate.topicPages.weLightenTheLoad" />
            </div>
            <div className={styles.featuresRow}>
              <div className={styles.feature}>
                <img
                  alt={intl.formatMessage({ id: 'accessibility.altTags.activateFeatureFamily' })}
                  className={styles.focusAreaImage}
                  src={imageUrl('activateTopicPageFeatureExpertSupport')}
                />
                <div className={styles.featureTitle}>
                  <FormattedMessage id="pages.activate.topicPages.features.expertGuide.title" />
                </div>
                <div className={styles.bodyCopy}>
                  <FormattedMessage id="pages.activate.topicPages.features.expertGuide.body" />
                </div>
              </div>

              <div className={styles.feature}>
                <img
                  alt={intl.formatMessage({ id: 'accessibility.altTags.activateFeatureFamily' })}
                  className={styles.focusAreaImage}
                  src={imageUrl('activateTopicPageFeatureSaveTime')}
                />
                <div className={styles.featureTitle}>
                  <FormattedMessage id="pages.activate.topicPages.features.saveTime.title" />
                </div>
                <div className={styles.bodyCopy}>
                  <FormattedMessage id="pages.activate.topicPages.features.saveTime.body" />
                </div>
              </div>

              <div className={styles.feature}>
                <img
                  alt={intl.formatMessage({ id: 'accessibility.altTags.activateFeatureFamily' })}
                  className={styles.focusAreaImage}
                  src={imageUrl('activateTopicPageFeatureEasyAccess')}
                />
                <div className={styles.featureTitle}>
                  <FormattedMessage id="pages.activate.topicPages.features.easyAccess.title" />
                </div>
                <div className={styles.bodyCopy}>
                  <FormattedMessage id="pages.activate.topicPages.features.easyAccess.body" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footerBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.hugeText}>
              <FormattedMessage id="pages.activate.getSupport" />
            </div>
            <div
              className={styles.activateButton}
              onClick={generateForwardToSignup(topic.toUpperCase() as ClientTopicOfInterestEnum)}
            >
              <FormattedMessage id={`pages.activate.topicPages.activateButton`} />
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ActivateTopicPage;
